import React, { useCallback, useEffect, useState } from 'react';
import { BellRing } from 'lucide-react';
import { Modal } from '../ui/Modal';
import { Field, Textarea } from '../ui/Field';
import { pillPrimary, pillSecondary } from '../ui/pills';
import { LIBELLES_ACTION, messageErreur, type DemandeAcces } from '../../lib/equipeApi';
import { useEquipe } from './EquipeShell';
import { dateCourte } from './droitsPhrase';

/**
 * Demandes d'accès en attente : un bandeau en haut de la liste des personnes, qui disparaît quand il n'y a rien à traiter.
 * (Les demandes déjà traitées restent consultables dans l'historique.)
 */
const PHRASE_ACTION: Record<string, string> = { voir: 'ouvrir', creer: 'ajouter des éléments dans', modifier: 'modifier', valider: 'valider dans', exporter: 'exporter depuis', sensibles: 'voir les données sensibles de' };

export const DemandesEnAttente: React.FC = () => {
    const { api } = useEquipe();
    const [liste, setListe] = useState<DemandeAcces[]>([]);
    const [refus, setRefus] = useState<DemandeAcces | null>(null);
    const [motif, setMotif] = useState('');
    const [erreurModale, setErreurModale] = useState('');
    const [message, setMessage] = useState('');

    const charger = useCallback(async () => {
        try { setListe((await api.demandes()).demandes.filter((d) => d.status === 'en_attente')); } catch { /* le bandeau est facultatif : la liste des personnes reste utilisable */ }
    }, [api]);
    useEffect(() => { void charger(); }, [charger]);

    const accepter = async (d: DemandeAcces) => {
        setMessage('');
        try { await api.decider(d.id, 'accepter'); setMessage(`${d.personne} a maintenant accès à « ${d.module_label} » (${LIBELLES_ACTION[d.action].toLowerCase()}).`); await charger(); }
        catch (e) { setMessage(messageErreur(e)); }
    };
    const refuser = async () => {
        if (!refus) return;
        try { await api.decider(refus.id, 'refuser', motif); setRefus(null); setMotif(''); setMessage(''); await charger(); }
        catch (e) { setErreurModale(messageErreur(e)); }
    };

    if (liste.length === 0 && !message) return null;
    return (
        <section aria-label="Demandes d'accès à traiter" className="rounded-xl border border-state-warn bg-state-warn-soft">
            {liste.length > 0 && (
                <div className="px-5 pt-4 pb-1 flex items-center gap-2 text-state-warn text-[14px] font-semibold">
                    <BellRing size={16} /> {liste.length === 1 ? '1 personne attend votre réponse' : `${liste.length} personnes attendent votre réponse`}
                </div>
            )}
            {message && <p role="status" className="px-5 py-3 text-[13px] font-medium text-content">{message}</p>}
            {liste.map((d) => (
                <div key={d.id} className="px-5 py-3 border-t border-border-soft first:border-t-0 flex flex-wrap items-center gap-3">
                    <div className="min-w-0 flex-1 basis-[260px]">
                        <div className="text-[13px] text-content"><b>{d.personne || 'Une personne de votre équipe'}</b> voudrait {PHRASE_ACTION[d.action] || 'accéder à'} « <b>{d.module_label}</b> »</div>
                        <div className="text-[11.5px] text-content-muted">{dateCourte(d.created_at)}{d.message ? ` · « ${d.message} »` : ''}</div>
                    </div>
                    <div className="flex gap-2">
                        <button type="button" className={`${pillSecondary(32)} max-md:!h-11`} onClick={() => { setRefus(d); setMotif(''); setErreurModale(''); }}>Refuser</button>
                        <button type="button" className={`${pillPrimary(32)} max-md:!h-11`} onClick={() => accepter(d)}>Accepter</button>
                    </div>
                </div>
            ))}
            <Modal open={!!refus} onClose={() => setRefus(null)} size="md" title="Refuser la demande" subtitle={refus ? `${refus.personne} · ${refus.module_label}` : undefined}
                footer={<><button type="button" className={pillSecondary(36)} onClick={() => setRefus(null)}>Annuler</button><button type="button" className={pillPrimary(36)} disabled={!motif.trim()} onClick={refuser}>Refuser la demande</button></>}>
                <Field label="Motif du refus" required help="Il reste enregistré avec la demande." error={erreurModale}>{(f) => <Textarea {...f} value={motif} onChange={(e) => setMotif(e.target.value)} maxLength={300} autoFocus />}</Field>
            </Modal>
        </section>
    );
};
